import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrophyIcon, TableCellsIcon, ViewColumnsIcon } from '@heroicons/react/24/solid';
import * as Flags from 'country-flag-icons/react/3x2';
import { hasFlag } from 'country-flag-icons';
import type { TvLeaderboardEntry, TvTournament, TvPartner, TvSpecialPrize } from '../hooks/useTvData';
import { formatScore as formatScoreShared, calcStrokePlayNet, calcStablefordNet, type ScoringMethod } from '../../lib/scoring';

interface Props {
  tournament: TvTournament | null;
  leaderboard: TvLeaderboardEntry[];
  partners?: TvPartner[];
  orgLogoUrl?: string | null;
  totalRounds?: number;
  roundNames?: Record<number, string>;
  specialPrizes?: TvSpecialPrize[];
  holeParMap?: Record<number, number>;
  holeHcpMap?: Record<number, number>;
}

const PAGE_SIZE = 10;

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const ALL_CODES = Object.keys(Flags) as (keyof typeof Flags)[];

function resolveCountryCode(country: string): string | null {
  const trimmed = country.trim();
  const upper = trimmed.toUpperCase();
  if (upper.length === 2 && hasFlag(upper)) return upper;
  for (const code of ALL_CODES) {
    try {
      if (regionNames.of(code)?.toLowerCase() === trimmed.toLowerCase()) return code;
    } catch { /* ignore */ }
  }
  return null;
}

function CountryFlag({ country, compact }: { country: string; compact?: boolean }) {
  const code = resolveCountryCode(country);
  if (!code) return null;
  const FlagComponent = Flags[code as keyof typeof Flags];
  return (
    <FlagComponent
      title={country.trim()}
      className={`rounded-sm flex-shrink-0 ${compact ? 'w-4 h-3' : 'w-5 h-3.5 lg:w-6 lg:h-4'}`}
    />
  );
}

const HOLE_PARS: Record<number, number> = {
  1: 4, 2: 4, 3: 3, 4: 5, 5: 4, 6: 3, 7: 4, 8: 5, 9: 4,
  10: 4, 11: 3, 12: 5, 13: 4, 14: 4, 15: 3, 16: 5, 17: 4, 18: 4,
};

const formatScore = (score: number | null, method: ScoringMethod = 'stroke_play_net'): string => {
  if (score == null) return '--';
  return formatScoreShared(score, method);
};

function computeRoundScoreVsPar(entry: TvLeaderboardEntry, round: number, holeParMap?: Record<number, number>, holeHcpMap?: Record<number, number>, method: ScoringMethod = 'stroke_play_net'): number | null {
  if (entry.round_scores && entry.round_scores[round - 1] != null) {
    return entry.round_scores[round - 1];
  }
  const roundHoles = (entry.hole_scores ?? []).filter((hs) => hs.round === round && hs.strokes > 0);
  if (roundHoles.length === 0) return null;
  const holes = roundHoles.map(hs => ({ hole: hs.hole, strokes: hs.strokes }));
  const parMap = holeParMap ?? HOLE_PARS;
  const hcpMap = holeHcpMap ?? Object.fromEntries(Object.keys(HOLE_PARS).map((h) => [Number(h), Number(h)]));
  const handicap = Math.floor(entry.handicap ?? 0);
  if (method === 'stableford_net') {
    return calcStablefordNet(holes, handicap, parMap, hcpMap);
  }
  return calcStrokePlayNet(holes, handicap, parMap, hcpMap);
}

function computeHolesPlayedInRound(entry: TvLeaderboardEntry, round: number): number {
  const roundHoles = (entry.hole_scores ?? []).filter((hs) => hs.round === round);
  if (roundHoles.length === 0) return 0;
  const holesWithStrokes = new Set(roundHoles.filter((hs) => hs.strokes > 0).map((hs) => hs.hole));
  if (holesWithStrokes.size >= 18) return 18;
  const isSubmitted = roundHoles.some((hs) => hs.score_status === 'submitted' || hs.score_status === 'verified');
  if (isSubmitted && roundHoles.length >= 18) return 18;
  return holesWithStrokes.size;
}

const positionColors: Record<number, string> = {
  1: 'bg-yellow-500 text-yellow-950',
  2: 'bg-gray-300 text-gray-900',
  3: 'bg-amber-600 text-white',
};

const TIER_BADGE: Record<string, string> = {
  'tournament host': 'text-green-300',
  'title sponsor': 'text-cyan-300',
  diamond: 'text-cyan-300',
  platinum: 'text-slate-200',
  gold: 'text-yellow-300',
  silver: 'text-gray-300',
  bronze: 'text-amber-300',
};

const FEATURED_CYCLE_INTERVAL = 7000;
const TICKER_SCROLL_DURATION = 18000;

const TIER_ORDER: Record<string, number> = {
  'tournament host': 0,
  'title sponsor': 1,
  diamond: 2,
  platinum: 3,
  gold: 4,
  silver: 5,
  bronze: 6,
};

function tierLabel(tier: string): string {
  const t = tier.toLowerCase();
  if (t.includes('sponsor') || t.includes('host') || t.includes('partner')) return tier;
  return `${tier} Sponsor`;
}

function SponsorLogo({ sponsor, size = 'sm' }: { sponsor: TvPartner; size?: 'sm' | 'md' }) {
  const dim = size === 'md' ? 'w-12 h-12 lg:w-16 lg:h-16' : 'w-8 h-8 lg:w-10 lg:h-10';
  const src = sponsor.logo_url ?? sponsor.image_url;
  return (
    <div className={`${dim} rounded-lg bg-white/8 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0`}>
      {src ? (
        <img src={src} alt={sponsor.company_name} className="w-full h-full object-contain p-1" />
      ) : (
        <span className="text-white/40 font-bold text-base">{sponsor.company_name.charAt(0)}</span>
      )}
    </div>
  );
}

function SponsorAdPanel({ partners }: { partners: TvPartner[] }) {
  const sorted = [...partners].sort((a, b) => {
    const ao = TIER_ORDER[a.tier.toLowerCase()] ?? 99;
    const bo = TIER_ORDER[b.tier.toLowerCase()] ?? 99;
    return ao - bo;
  });

  const [featuredIndex, setFeaturedIndex] = useState(0);

  useEffect(() => {
    if (sorted.length <= 1) return;
    const timer = setInterval(() => {
      setFeaturedIndex((i) => (i + 1) % sorted.length);
    }, FEATURED_CYCLE_INTERVAL);
    return () => clearInterval(timer);
  }, [sorted.length]);

  if (sorted.length === 0) return null;

  const featured = sorted[featuredIndex];
  const featuredTier = featured.tier.toLowerCase();
  const featuredTierColor = TIER_BADGE[featuredTier] ?? 'text-white/60';
  const featuredImg = featured.image_url ?? featured.logo_url;

  return (
    <div className="hidden lg:flex w-56 xl:w-72 2xl:w-80 flex-shrink-0 flex-col bg-black/40 border-l border-white/10 overflow-hidden">
      <div className="px-3 xl:px-4 py-2 xl:py-3 border-b border-white/10 bg-gradient-to-r from-golf-green/30 to-transparent flex items-center gap-2">
        <div className="w-1.5 h-1.5 rounded-full bg-golf-orange animate-pulse" />
        <p className="text-white/60 text-[10px] xl:text-xs font-bold uppercase tracking-widest">
          Official Sponsors
        </p>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="relative flex-1 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={featured.id}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              className="absolute inset-0 flex flex-col"
            >
              <div className="relative w-full h-28 xl:h-36 2xl:h-44 overflow-hidden bg-black/30 flex-shrink-0">
                {featuredImg ? (
                  <img
                    src={featuredImg}
                    alt={featured.company_name}
                    className="w-full h-full object-cover opacity-70"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-6xl font-bold text-white/10">
                      {featured.company_name.charAt(0)}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-2 xl:bottom-3 left-3 xl:left-4 right-3 xl:right-4 flex items-end gap-2 xl:gap-3">
                  <SponsorLogo sponsor={featured} size="md" />
                  <div className="min-w-0">
                    <span className={`text-[9px] xl:text-[10px] font-bold uppercase tracking-widest ${featuredTierColor}`}>
                      {tierLabel(featured.tier)}
                    </span>
                    <h3 className="text-white font-bold text-sm xl:text-base 2xl:text-lg leading-tight truncate">
                      {featured.company_name}
                    </h3>
                  </div>
                </div>
              </div>

              <div className="px-3 xl:px-4 py-2 xl:py-3 flex flex-col gap-2 xl:gap-3 flex-1">
                {featured.description ? (
                  <p className="text-white/55 text-[11px] xl:text-xs 2xl:text-sm leading-relaxed line-clamp-3">
                    {featured.description}
                  </p>
                ) : (
                  <p className="text-white/25 text-[11px] italic">Proud sponsor of this tournament</p>
                )}

                {featured.stats && featured.stats.length > 0 && (
                  <div className="mt-auto flex flex-col gap-1.5 xl:gap-2">
                    <p className="text-white/30 text-[9px] xl:text-[10px] font-bold uppercase tracking-widest">
                      Partnership Statistics
                    </p>
                    <div className="grid grid-cols-2 gap-1.5 xl:gap-2">
                      {featured.stats.map((stat, i) => (
                        <div key={i} className="bg-white/8 border border-white/10 rounded-lg px-1.5 xl:px-2 py-1 xl:py-1.5 text-center">
                          <p className={`font-bold text-[10px] xl:text-xs leading-tight ${featuredTierColor}`}>{stat}</p>
                        </div>
                      ))}
                    </div>
                    {featured.website && (
                      <p className="text-white/25 text-[9px] xl:text-[10px] truncate mt-1">
                        {featured.website.replace(/^https?:\/\//, '')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-center gap-1.5 py-2 flex-shrink-0">
          {sorted.map((_, i) => (
            <button
              key={i}
              onClick={() => setFeaturedIndex(i)}
              className={`rounded-full transition-all duration-300 ${
                i === featuredIndex
                  ? 'w-6 h-1.5 bg-golf-orange'
                  : 'w-1.5 h-1.5 bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>

        <div className="border-t border-white/10 flex-shrink-0">
          <div className="px-3 xl:px-4 pt-2 pb-1 flex items-center justify-between">
            <p className="text-white/30 text-[9px] xl:text-[10px] font-semibold uppercase tracking-widest">
              All Sponsors
            </p>
            <span className="text-white/25 text-[9px] xl:text-[10px]">{sorted.length} partners</span>
          </div>
          <div className="relative overflow-hidden bg-white mx-2 xl:mx-3 mb-2 xl:mb-3 rounded-lg" style={{ height: 48 }}>
            <motion.div
              className="flex items-center gap-4 px-3 h-full"
              animate={{ x: [0, -(sorted.length * 72)] }}
              transition={{
                duration: TICKER_SCROLL_DURATION / 1000,
                repeat: Infinity,
                ease: 'linear',
                repeatType: 'loop',
              }}
              style={{ width: 'max-content' }}
            >
              {[...sorted, ...sorted].map((s, i) => {
                const logoSrc = s.logo_url ?? s.image_url;
                return (
                  <div
                    key={`${s.id}-${i}`}
                    className={`flex flex-col items-center gap-0.5 cursor-pointer transition-all duration-200 flex-shrink-0 ${
                      s.id === featured.id ? 'opacity-100 scale-105' : 'opacity-50 hover:opacity-80'
                    }`}
                    onClick={() => setFeaturedIndex(sorted.indexOf(s) % sorted.length)}
                  >
                    <div className="w-7 h-7 xl:w-8 xl:h-8 flex items-center justify-center">
                      {logoSrc ? (
                        <img src={logoSrc} alt={s.company_name} className="w-full h-full object-contain" />
                      ) : (
                        <span className="text-gray-700 font-bold text-xs xl:text-sm">{s.company_name.charAt(0)}</span>
                      )}
                    </div>
                    <span className="text-gray-500 text-[7px] font-medium text-center leading-tight max-w-[48px] truncate">
                      {s.company_name}
                    </span>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface PlayerRowProps {
  entry: TvLeaderboardEntry;
  idx: number;
  compact?: boolean;
  mobile?: boolean;
  activeRound?: number;
  method?: ScoringMethod;
  holeParMap?: Record<number, number>;
  holeHcpMap?: Record<number, number>;
}

function PlayerRow({ entry, idx, compact = false, mobile = false, activeRound = 1, method = 'stroke_play_net', holeParMap, holeHcpMap }: PlayerRowProps) {
  const posColor = positionColors[entry.position] ?? 'bg-white/10 text-white';
  const isTop3 = entry.position <= 3;
  const roundScore = computeRoundScoreVsPar(entry, activeRound, holeParMap, holeHcpMap, method);
  const holesPlayed = computeHolesPlayedInRound(entry, activeRound);

  if (mobile) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
        isTop3
          ? 'bg-gradient-to-r from-white/10 to-white/5 border border-white/10'
          : idx % 2 === 0 ? 'bg-white/5' : 'bg-transparent'
      }`}>
        <span className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${posColor}`}>
          {entry.position}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className={`font-semibold text-sm truncate ${isTop3 ? 'text-white' : 'text-white/90'}`}>
              {entry.player_name}
            </p>
            {entry.country && <CountryFlag country={entry.country} compact />}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-white/40 text-[10px]">HCP {entry.handicap}</span>
            {entry.category && (
              <span className="inline-block px-1.5 py-px rounded-full bg-golf-green/50 text-green-300 font-semibold uppercase text-[8px]">
                {entry.category}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-white/50 text-[10px]">
            {holesPlayed > 0 && holesPlayed < 18 ? `Thru ${holesPlayed}` : holesPlayed === 18 ? 'F' : '--'}
          </span>
          <span className={`font-bold text-base font-display ${
            roundScore == null ? 'text-white/30' : roundScore < 0 ? 'text-green-400' : roundScore > 0 ? 'text-red-400' : 'text-white'
          }`}>
            {formatScore(roundScore, method)}
          </span>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className={`grid grid-cols-[1.5rem_1fr_3rem_3rem] lg:grid-cols-[2rem_1fr_3.5rem_3.5rem] xl:grid-cols-[2.5rem_1fr_4rem_4rem] items-center px-2 lg:px-3 rounded-xl transition-all py-1.5 lg:py-2 ${
        isTop3
          ? 'bg-gradient-to-r from-white/10 to-white/5 border border-white/10'
          : idx % 2 === 0 ? 'bg-white/5' : 'bg-transparent'
      }`}>
        <div className="flex items-center">
          <span className={`w-5 h-5 lg:w-6 lg:h-6 xl:w-7 xl:h-7 rounded-full flex items-center justify-center font-bold text-[9px] lg:text-[10px] xl:text-xs ${posColor}`}>
            {entry.position}
          </span>
        </div>
        <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
          <p className={`font-semibold truncate text-xs lg:text-sm leading-tight ${isTop3 ? 'text-white' : 'text-white/90'}`}>
            {entry.player_name}
          </p>
          <span className="text-white/40 font-medium flex-shrink-0 text-[9px] lg:text-[10px]">
            {entry.handicap}
          </span>
          {entry.category && (
            <span className="inline-block px-1 py-px rounded-full bg-golf-green/50 text-green-300 font-semibold uppercase text-[7px] lg:text-[8px] flex-shrink-0">
              {entry.category}
            </span>
          )}
        </div>
        <div className="text-[10px] lg:text-xs text-white/70 text-center">
          {holesPlayed > 0 && holesPlayed < 18 ? `Thru ${holesPlayed}` : holesPlayed === 18 ? 'F' : '--'}
        </div>
        <div className="text-right">
          <span className={`font-bold font-display text-sm lg:text-base xl:text-lg ${
            roundScore == null ? 'text-white/30' : roundScore < 0 ? 'text-green-400' : roundScore > 0 ? 'text-red-400' : 'text-white'
          }`}>
            {formatScore(roundScore, method)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-[3rem_1fr_8rem_7rem_5rem] lg:grid-cols-[3.5rem_1fr_10rem_9rem_6rem] xl:grid-cols-[4rem_1fr_12rem_10rem_7rem] items-center px-3 lg:px-4 rounded-xl transition-all py-3 lg:py-4 ${
      isTop3
        ? 'bg-gradient-to-r from-white/10 to-white/5 border border-white/10'
        : idx % 2 === 0 ? 'bg-white/5' : 'bg-transparent'
    }`}>
      <div className="flex items-center">
        <span className={`w-8 h-8 lg:w-9 lg:h-9 xl:w-10 xl:h-10 rounded-full flex items-center justify-center font-bold text-xs lg:text-sm ${posColor}`}>
          {entry.position}
        </span>
      </div>
      <div className="flex items-baseline gap-2 min-w-0">
        <p className={`font-semibold truncate text-base lg:text-xl xl:text-2xl ${isTop3 ? 'text-white' : 'text-white/90'}`}>
          {entry.player_name}
        </p>
        <span className="text-white/45 font-medium flex-shrink-0 text-xs lg:text-sm">
          HCP {entry.handicap}
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {entry.country && <CountryFlag country={entry.country} />}
        {entry.category && (
          <span className="inline-block px-2 py-0.5 rounded-full bg-golf-green/50 text-green-300 font-semibold uppercase text-[9px] lg:text-[10px]">
            {entry.category}
          </span>
        )}
      </div>
      <div className="text-sm lg:text-base text-white/70">
        {holesPlayed > 0 && holesPlayed < 18 ? `Thru ${holesPlayed}` : holesPlayed === 18 ? 'F' : '--'}
      </div>
      <div className="text-right">
        <span className={`font-bold font-display text-xl lg:text-2xl xl:text-3xl ${
          roundScore == null ? 'text-white/30' : roundScore < 0 ? 'text-green-400' : roundScore > 0 ? 'text-red-400' : 'text-white'
        }`}>
          {formatScore(roundScore, method)}
        </span>
      </div>
    </div>
  );
}

const COL_HEADERS = ['Pos', 'Player', 'Country / Cat', 'Thru', 'Score'];

function MobileLeaderboardList({ entries, page, totalPages, onPageChange, activeRound = 1, method = 'stroke_play_net' }: {
  entries: TvLeaderboardEntry[];
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  activeRound?: number;
  method?: ScoringMethod;
}) {
  const pageEntries = entries.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  return (
    <div className="flex-1 flex flex-col overflow-hidden px-3 py-3 gap-2">
      <AnimatePresence mode="wait">
        <motion.div
          key={page}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4 }}
          className="flex-1 flex flex-col gap-1.5 overflow-y-auto"
        >
          {pageEntries.map((entry, idx) => (
            <PlayerRow key={entry.id} entry={entry} idx={idx} mobile activeRound={activeRound} method={method} />
          ))}
          {pageEntries.length === 0 && (
            <div className="flex items-center justify-center flex-1">
              <p className="text-white/40 text-lg">No scores available yet</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1.5 flex-shrink-0 pt-1">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              onClick={() => onPageChange(i)}
              className={`rounded-full transition-all duration-300 ${
                i === page ? 'w-6 h-2 bg-golf-orange' : 'w-2 h-2 bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ColHeader({ compact }: { compact?: boolean }) {
  return (
    <div className={`grid items-center mb-2 px-2 lg:px-3 flex-shrink-0 ${
      compact
        ? 'grid-cols-[2rem_1fr_5rem_4rem_3rem] lg:grid-cols-[2.5rem_1fr_6rem_5rem_3.5rem] xl:grid-cols-[3rem_1fr_7rem_6rem_4rem]'
        : 'grid-cols-[3rem_1fr_8rem_7rem_5rem] lg:grid-cols-[3.5rem_1fr_10rem_9rem_6rem] xl:grid-cols-[4rem_1fr_12rem_10rem_7rem]'
    }`}>
      {COL_HEADERS.map((h, i) => (
        <span
          key={h}
          className={`text-white/40 text-[9px] lg:text-[10px] xl:text-xs font-semibold uppercase tracking-widest ${i === COL_HEADERS.length - 1 ? 'text-right' : ''}`}
        >
          {h}
        </span>
      ))}
    </div>
  );
}

function scoreCellColor(score: number | null | undefined): string {
  if (score == null) return 'text-white/30';
  if (score < -1) return 'text-cyan-300 font-bold';
  if (score === -1) return 'text-green-400 font-bold';
  if (score === 0) return 'text-white';
  if (score === 1) return 'text-red-400';
  return 'text-red-500 font-bold';
}

function scoreCellBg(score: number | null | undefined): string {
  if (score == null) return '';
  if (score < -1) return 'bg-cyan-500/20 ring-1 ring-cyan-500/40';
  if (score === -1) return 'bg-green-500/20 ring-1 ring-green-500/30';
  if (score === 0) return 'bg-white/8';
  if (score === 1) return 'bg-red-500/15 ring-1 ring-red-500/20';
  return 'bg-red-500/25 ring-1 ring-red-500/30';
}

function holeScoreVsPar(strokes: number, hole: number, par?: number | null): number {
  return strokes - (par ?? HOLE_PARS[hole] ?? 4);
}

function strokeCellColor(diff: number): string {
  if (diff <= -2) return 'text-cyan-200 font-bold';
  if (diff === -1) return 'text-green-400 font-bold';
  if (diff === 0) return 'text-white/80';
  if (diff === 1) return 'text-red-400';
  return 'text-red-500 font-bold';
}

function strokeCellBg(diff: number): string {
  if (diff <= -2) return 'bg-cyan-500/25 ring-1 ring-cyan-400/50';
  if (diff === -1) return 'bg-green-500/20 ring-1 ring-green-400/40';
  if (diff === 0) return 'bg-white/8';
  if (diff === 1) return 'bg-red-500/15 ring-1 ring-red-400/30';
  return 'bg-red-500/30 ring-1 ring-red-400/50';
}

const HOLE_CYCLE_INTERVAL = 12000;

function ScorecardLayout({
  played,
  hasSponsors,
  partners,
  totalRoundsProp,
  roundNames = {},
  method = 'stroke_play_net',
  holeParMap: propParMap,
  holeHcpMap: propHcpMap,
}: {
  played: TvLeaderboardEntry[];
  hasSponsors: boolean;
  partners: TvPartner[];
  tournament: TvTournament | null;
  totalRoundsProp?: number;
  roundNames?: Record<number, string>;
  method?: ScoringMethod;
  holeParMap?: Record<number, number>;
  holeHcpMap?: Record<number, number>;
}) {
  const parMap = propParMap && Object.keys(propParMap).length > 0 ? propParMap : HOLE_PARS;
  const maxRounds = played.reduce((m, e) => Math.max(m, e.round_scores?.length ?? 0), 0);
  const numRounds = Math.max(maxRounds, totalRoundsProp ?? 0, 1);
  const latestRoundWithScores = maxRounds > 0 ? maxRounds : 1;
  const [activeRound, setActiveRound] = useState(latestRoundWithScores);
  const [holeWindow, setHoleWindow] = useState<'front' | 'back'>('front');

  const hasPairingsData = played.some((e) => (e.drawn_rounds ?? []).length > 0);
  const roundPlayers = played
    .filter((e) => {
      if (!hasPairingsData) return e.overall_status === 'PLAYED';
      const drawn = e.drawn_rounds ?? [];
      return drawn.length === 0 || drawn.includes(activeRound);
    })
    .sort((a, b) => {
      const aScore = computeRoundScoreVsPar(a, activeRound, propParMap, propHcpMap, method);
      const bScore = computeRoundScoreVsPar(b, activeRound, propParMap, propHcpMap, method);
      if (aScore == null && bScore == null) return 0;
      if (aScore == null) return 1;
      if (bScore == null) return -1;
      if (method === 'stableford_net') return bScore - aScore;
      return aScore - bScore;
    })
    .map((entry, idx) => ({ ...entry, position: idx + 1 }));
  const top15 = roundPlayers.slice(0, 15);

  useEffect(() => {
    setActiveRound(latestRoundWithScores);
  }, [latestRoundWithScores]);

  useEffect(() => {
    const timer = setInterval(() => {
      setHoleWindow((w) => w === 'front' ? 'back' : 'front');
    }, HOLE_CYCLE_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  const holes = holeWindow === 'front'
    ? Array.from({ length: 9 }, (_, i) => i + 1)
    : Array.from({ length: 9 }, (_, i) => i + 10);

  const holeCols = `2.5rem minmax(0,1fr) ${holes.map(() => '2.2rem').join(' ')} 3rem 3.5rem`;

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex-1 overflow-hidden flex flex-col px-3 md:px-5 lg:px-8 py-2 md:py-3 lg:py-4">

        <div className="flex items-center justify-between mb-2 flex-shrink-0 px-1">
          <div className="flex items-center gap-1">
            {Array.from({ length: numRounds }, (_, i) => (
              <button
                key={i}
                onClick={() => setActiveRound(i + 1)}
                className={`px-2.5 py-1 rounded-lg text-[10px] lg:text-xs font-bold uppercase tracking-wide transition-all ${
                  activeRound === i + 1
                    ? 'bg-golf-orange text-white'
                    : 'bg-white/10 text-white/50 hover:bg-white/15'
                }`}
              >
                {roundNames[i + 1] || `Round ${i + 1}`}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-black/30 rounded-lg p-0.5 border border-white/10">
            <button
              onClick={() => setHoleWindow('front')}
              className={`px-2.5 py-1 rounded-md text-[10px] lg:text-xs font-bold transition-all ${
                holeWindow === 'front' ? 'bg-golf-orange text-white' : 'text-white/50 hover:text-white'
              }`}
            >
              Front 9
            </button>
            <button
              onClick={() => setHoleWindow('back')}
              className={`px-2.5 py-1 rounded-md text-[10px] lg:text-xs font-bold transition-all ${
                holeWindow === 'back' ? 'bg-golf-orange text-white' : 'text-white/50 hover:text-white'
              }`}
            >
              Back 9
            </button>
          </div>
        </div>

        <div
          className="grid items-center mb-1 px-1 flex-shrink-0 gap-x-1"
          style={{ gridTemplateColumns: holeCols }}
        >
          <span className="text-white/35 text-[9px] font-semibold uppercase tracking-widest">Pos</span>
          <span className="text-white/35 text-[9px] font-semibold uppercase tracking-widest">Player</span>
          {holes.map((h) => (
            <div key={h} className="flex flex-col items-center gap-px">
              <span className="text-white/35 text-[9px] font-bold text-center">{h}</span>
              <span className="text-white/20 text-[8px] text-center">p{parMap[h] ?? HOLE_PARS[h]}</span>
            </div>
          ))}
          <span className="text-white/35 text-[9px] font-semibold uppercase text-center">
            {holeWindow === 'front' ? 'Out' : 'In'}
          </span>
          <span className="text-white/35 text-[9px] font-semibold uppercase tracking-widest text-right">Tot</span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeRound}-${holeWindow}`}
            initial={{ opacity: 0, x: holeWindow === 'back' ? 20 : -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col gap-px flex-1"
          >
            {top15.length === 0 && (
              <div className="flex items-center justify-center h-full">
                <p className="text-white/40 text-xl lg:text-2xl">No scores available yet</p>
              </div>
            )}
            {top15.map((entry, idx) => {
              const posColor = positionColors[entry.position] ?? 'bg-white/10 text-white';
              const isTop3 = entry.position <= 3;
              const entryHoles = (entry.hole_scores ?? []).filter((hs) => hs.round === activeRound);
              const holeMap: Record<number, typeof entryHoles[0]> = {};
              entryHoles.forEach((hs) => { holeMap[hs.hole] = hs; });

              const halfScore = holes.reduce((sum, h) => {
                const hs = holeMap[h];
                return hs ? sum + hs.strokes : sum;
              }, 0);
              const halfPar = holes.reduce((sum, h) => sum + (parMap[h] ?? HOLE_PARS[h] ?? 4), 0);
              const halfDiff = halfScore > 0 ? halfScore - halfPar : null;

              return (
                <div
                  key={entry.id}
                  className={`grid items-center px-1 rounded-lg py-1 lg:py-1.5 gap-x-1 transition-all ${
                    isTop3
                      ? 'bg-gradient-to-r from-white/10 to-white/5 border border-white/10'
                      : idx % 2 === 0 ? 'bg-white/5' : 'bg-transparent'
                  }`}
                  style={{ gridTemplateColumns: holeCols }}
                >
                  <div className="flex items-center">
                    <span className={`w-6 h-6 lg:w-7 lg:h-7 rounded-full flex items-center justify-center font-bold text-[10px] flex-shrink-0 ${posColor}`}>
                      {entry.position}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5 min-w-0">
                    {entry.country && <CountryFlag country={entry.country} compact />}
                    <p className={`font-semibold truncate text-xs lg:text-sm leading-tight ${isTop3 ? 'text-white' : 'text-white/90'}`}>
                      {entry.player_name}
                    </p>
                    <span className="text-white/40 text-[9px] lg:text-[10px] flex-shrink-0 font-medium">
                      HCP {entry.handicap}
                    </span>
                    {entry.category && (
                      <span className="inline-block px-1 py-px rounded-full bg-golf-green/50 text-green-300 font-semibold uppercase text-[7px] lg:text-[8px] flex-shrink-0">
                        {entry.category}
                      </span>
                    )}
                  </div>

                  {holes.map((h) => {
                    const hs = holeMap[h];
                    if (!hs) {
                      return (
                        <div key={h} className="flex items-center justify-center">
                          <span className="w-7 h-7 lg:w-8 lg:h-8 rounded flex items-center justify-center text-[10px] text-white/15 bg-white/4">
                            -
                          </span>
                        </div>
                      );
                    }
                    const diff = holeScoreVsPar(hs.strokes, h, hs.par);
                    return (
                      <div key={h} className="flex items-center justify-center">
                        <span className={`w-7 h-7 lg:w-8 lg:h-8 rounded flex items-center justify-center text-[11px] lg:text-xs font-bold transition-all ${strokeCellColor(diff)} ${strokeCellBg(diff)}`}>
                          {hs.strokes}
                        </span>
                      </div>
                    );
                  })}

                  <div className="flex items-center justify-center">
                    {halfDiff !== null ? (
                      <span className={`text-xs lg:text-sm font-bold ${
                        halfDiff < 0 ? 'text-green-400' : halfDiff > 0 ? 'text-red-400' : 'text-white/70'
                      }`}>
                        {halfDiff === 0 ? 'E' : halfDiff > 0 ? `+${halfDiff}` : halfDiff}
                      </span>
                    ) : (
                      <span className="text-white/20 text-xs">-</span>
                    )}
                  </div>

                  {(() => {
                    const roundScore = computeRoundScoreVsPar(entry, activeRound, propParMap, propHcpMap, method);
                    return (
                      <div className="text-right pr-1">
                        <span className={`font-bold font-display text-sm lg:text-base xl:text-lg ${
                          roundScore == null ? 'text-white/30' : roundScore < 0 ? 'text-green-400' : roundScore > 0 ? 'text-red-400' : 'text-white'
                        }`}>
                          {formatScore(roundScore, method)}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </motion.div>
        </AnimatePresence>

        <div className="mt-1.5 flex-shrink-0 flex items-center justify-between px-1 border-t border-white/10 pt-1.5">
          <div className="flex items-center gap-3 text-[9px] text-white/35 font-medium flex-wrap">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-cyan-500/25 ring-1 ring-cyan-400/50 inline-block" /> Eagle−</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-green-500/20 ring-1 ring-green-400/40 inline-block" /> Birdie</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-white/8 inline-block" /> Par</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-500/15 ring-1 ring-red-400/30 inline-block" /> Bogey</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-500/30 ring-1 ring-red-400/50 inline-block" /> Dbl+</span>
          </div>
          <span className="text-white/20 text-[9px]">
            Holes {holes[0]}–{holes[8]} · Par {holes.reduce((s, h) => s + HOLE_PARS[h], 0)}
          </span>
        </div>
      </div>

      {hasSponsors && <SponsorAdPanel partners={partners} />}
    </div>
  );
}

function PaginatedLayout({
  played,
  hasSponsors,
  partners,
  page,
  activeRound = 1,
  method = 'stroke_play_net',
}: {
  played: TvLeaderboardEntry[];
  hasSponsors: boolean;
  partners: TvPartner[];
  tournament: TvTournament | null;
  page: number;
  activeRound?: number;
  method?: ScoringMethod;
}) {
  const pageEntries = played.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex-1 overflow-hidden flex flex-col px-4 md:px-8 lg:px-12 xl:px-16 py-3 md:py-5 lg:py-6">
        <ColHeader />
        <AnimatePresence mode="wait">
          <motion.div
            key={page}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col gap-1 lg:gap-1.5 flex-1"
          >
            {pageEntries.map((entry, idx) => (
              <PlayerRow key={entry.id} entry={entry} idx={idx} activeRound={activeRound} method={method} />
            ))}
            {pageEntries.length === 0 && (
              <div className="flex items-center justify-center h-full">
                <p className="text-white/40 text-xl lg:text-2xl">No scores available yet</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {hasSponsors && <SponsorAdPanel partners={partners} />}
    </div>
  );
}

const SCROLL_PAGE_SIZE = 20;
const SCROLL_INTERVAL = 6000;

function ScrollingColumnSection({ entries, activeRound = 1, method = 'stroke_play_net' }: { entries: TvLeaderboardEntry[]; activeRound?: number; method?: ScoringMethod }) {
  const [scrollPage, setScrollPage] = useState(0);
  const totalScrollPages = Math.max(1, Math.ceil(entries.length / SCROLL_PAGE_SIZE));
  const visibleEntries = entries.slice(scrollPage * SCROLL_PAGE_SIZE, (scrollPage + 1) * SCROLL_PAGE_SIZE);

  const startPos = scrollPage * SCROLL_PAGE_SIZE + 31;
  const endPos = Math.min((scrollPage + 1) * SCROLL_PAGE_SIZE + 30, entries.length + 30);

  useEffect(() => {
    if (totalScrollPages <= 1) return;
    const timer = setInterval(() => {
      setScrollPage((p) => (p + 1) % totalScrollPages);
    }, SCROLL_INTERVAL);
    return () => clearInterval(timer);
  }, [totalScrollPages]);

  return (
    <div className="flex flex-col min-w-0 flex-1 overflow-hidden h-full">
      <ColHeader compact />
      <div className="flex-1 flex flex-col overflow-hidden">
        {entries.length === 0 ? (
          <div className="flex-1 flex items-center justify-center opacity-30">
            <p className="text-white text-sm">No additional players</p>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={scrollPage}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="flex-1 flex flex-col gap-0.5 lg:gap-1 overflow-hidden"
            >
              {visibleEntries.map((entry, idx) => (
                <div key={entry.id} className="flex-1 flex flex-col justify-center min-h-0">
                  <PlayerRow entry={entry} idx={idx} compact activeRound={activeRound} method={method} />
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      <div className="mt-1 px-2 lg:px-3 flex items-center justify-between border-t border-white/10 pt-1 flex-shrink-0">
        <p className="text-white/30 text-[9px] lg:text-[10px] font-semibold uppercase tracking-widest">
          {entries.length === 0 ? 'Pos 31+' : `Pos ${startPos}–${endPos}`}
        </p>
        {totalScrollPages > 1 && (
          <div className="flex items-center gap-1">
            {Array.from({ length: totalScrollPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setScrollPage(i)}
                className={`rounded-full transition-all duration-300 ${
                  i === scrollPage ? 'w-4 h-1.5 bg-golf-orange' : 'w-1.5 h-1.5 bg-white/20 hover:bg-white/40'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ColumnSection({ label, entries, fillHeight = false, activeRound = 1, method = 'stroke_play_net' }: {
  label: string;
  entries: TvLeaderboardEntry[];
  fillHeight?: boolean;
  activeRound?: number;
  method?: ScoringMethod;
}) {
  return (
    <div className={`flex flex-col min-w-0 ${fillHeight ? 'flex-1' : 'flex-1'} overflow-hidden`}>
      <ColHeader compact />
      <div className={fillHeight ? 'flex-1 flex flex-col overflow-hidden' : 'flex-1 flex flex-col gap-0.5 lg:gap-1 overflow-y-auto'}>
        {entries.map((entry, idx) => (
          <div key={entry.id} className={fillHeight ? 'flex-1 flex flex-col justify-center min-h-0' : 'flex-shrink-0'}>
            <PlayerRow entry={entry} idx={idx} compact activeRound={activeRound} method={method} />
          </div>
        ))}
      </div>
      <div className="mt-1 px-2 lg:px-3 flex-shrink-0">
        <p className="text-white/30 text-[9px] lg:text-[10px] font-semibold uppercase tracking-widest border-t border-white/10 pt-1">
          {label}
        </p>
      </div>
    </div>
  );
}

function SplitLayout({
  played,
  hasSponsors,
  partners,
  totalRoundsProp,
  roundNames = {},
  method = 'stroke_play_net',
  holeParMap: propParMap,
  holeHcpMap: propHcpMap,
}: {
  played: TvLeaderboardEntry[];
  hasSponsors: boolean;
  partners: TvPartner[];
  tournament: TvTournament | null;
  activeRound?: number;
  totalRoundsProp?: number;
  roundNames?: Record<number, string>;
  method?: ScoringMethod;
  holeParMap?: Record<number, number>;
  holeHcpMap?: Record<number, number>;
}) {
  const maxRounds = played.reduce((m, e) => Math.max(m, e.round_scores?.length ?? 0), 0);
  const numRounds = Math.max(maxRounds, totalRoundsProp ?? 0, 1);
  const latestRoundWithScores = maxRounds > 0 ? maxRounds : 1;
  const [selectedRound, setSelectedRound] = useState(latestRoundWithScores);

  useEffect(() => {
    setSelectedRound(latestRoundWithScores);
  }, [latestRoundWithScores]);

  const hasPairingsData = played.some((e) => (e.drawn_rounds ?? []).length > 0);
  const roundPlayers = played
    .filter((e) => {
      if (!hasPairingsData) return e.overall_status === 'PLAYED';
      const drawn = e.drawn_rounds ?? [];
      return drawn.length === 0 || drawn.includes(selectedRound);
    })
    .sort((a, b) => {
      const aScore = computeRoundScoreVsPar(a, selectedRound, propParMap, propHcpMap, method);
      const bScore = computeRoundScoreVsPar(b, selectedRound, propParMap, propHcpMap, method);
      if (aScore == null && bScore == null) return 0;
      if (aScore == null) return 1;
      if (bScore == null) return -1;
      if (method === 'stableford_net') return bScore - aScore;
      return aScore - bScore;
    })
    .map((entry, idx) => ({ ...entry, position: idx + 1 }));

  const top10 = roundPlayers.slice(0, 10);
  const next20 = roundPlayers.slice(10, 30);
  const remaining = roundPlayers.slice(30);

  return (
    <div className="flex flex-1 overflow-hidden flex-col">
      <div className="flex items-center gap-2 px-3 md:px-5 lg:px-8 pt-2 md:pt-3 lg:pt-4 flex-shrink-0">
        <div className="flex items-center gap-1">
          {Array.from({ length: numRounds }, (_, i) => (
            <button
              key={i}
              onClick={() => setSelectedRound(i + 1)}
              className={`px-2.5 py-1 rounded-lg text-[10px] lg:text-xs font-bold uppercase tracking-wide transition-all ${
                selectedRound === i + 1
                  ? 'bg-golf-orange text-white'
                  : 'bg-white/10 text-white/50 hover:bg-white/15'
              }`}
            >
              {roundNames[i + 1] || `Round ${i + 1}`}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-hidden flex gap-0 px-2 md:px-4 lg:px-6 py-2 lg:py-4 h-full">
          <div className="flex-1 min-w-0 pr-2 lg:pr-4 border-r border-white/10 flex flex-col">
            <ColumnSection label="Positions 1\u201310" entries={top10} fillHeight activeRound={selectedRound} method={method} />
          </div>

          <div className="flex-1 min-w-0 px-2 lg:px-4 border-r border-white/10 flex flex-col overflow-hidden">
            <ColumnSection label="Positions 11\u201330" entries={next20} activeRound={selectedRound} method={method} />
          </div>

          <div className="flex-1 min-w-0 pl-2 lg:pl-4 flex flex-col overflow-hidden">
            <ScrollingColumnSection entries={remaining} activeRound={selectedRound} method={method} />
          </div>
        </div>

        {hasSponsors && <SponsorAdPanel partners={partners} />}
      </div>
    </div>
  );
}

const PRIZE_LABEL: Record<string, string> = {
  nearest_pin: 'Nearest to Pin',
  longest_drive: 'Longest Drive',
  hole_in_one: 'Hole-in-One',
};

function SpecialPrizesStrip({ prizes }: { prizes: TvSpecialPrize[] }) {
  if (prizes.length === 0) return null;

  return (
    <div className="flex-shrink-0 border-t border-white/10 bg-black/40 px-4 lg:px-8 py-2 overflow-hidden">
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <TrophyIcon className="w-4 h-4 text-golf-orange" />
          <span className="text-white/50 text-[10px] lg:text-xs font-bold uppercase tracking-widest">Special Prizes</span>
        </div>
        <div className="flex items-center gap-4 lg:gap-6 overflow-x-auto no-scrollbar">
          {prizes.map((prize) => (
            <div key={prize.id} className="flex items-center gap-2 flex-shrink-0">
              <span className="text-white/40 text-[10px] lg:text-xs font-medium">
                {PRIZE_LABEL[prize.prize_type]} ({prize.gender === 'men' ? 'M' : 'L'})
                {prize.hole_number && ` H${prize.hole_number}`}:
              </span>
              <span className="text-white text-[11px] lg:text-sm font-semibold">
                {prize.winner_name || 'TBD'}
              </span>
              {prize.distance && (
                <span className="text-golf-orange text-[10px] lg:text-xs font-medium">
                  ({prize.distance})
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

type LayoutMode = 'scorecard' | 'split';

export default function TvLeaderboardSlide({ tournament, leaderboard, partners = [], orgLogoUrl, totalRounds: totalRoundsProp, roundNames = {}, specialPrizes = [], holeParMap: propHoleParMap, holeHcpMap: propHoleHcpMap }: Props) {
  const [layout, setLayout] = useState<LayoutMode>('scorecard');
  const [page, setPage] = useState(0);
  const method: ScoringMethod = (tournament?.scoring_method as ScoringMethod) || 'stroke_play_net';

  const activeRound = leaderboard.reduce((max, e) => {
    const r = e.current_round ?? e.rounds_played ?? 0;
    return r > max ? r : max;
  }, 0) || 1;

  const hasPairingsData = leaderboard.some((e) => (e.drawn_rounds ?? []).length > 0);

  const allPlayed = leaderboard.filter((e) =>
    e.overall_status === 'PLAYED' || (e.drawn_rounds ?? []).length > 0
  );

  const played = allPlayed
    .filter((e) => {
      if (!hasPairingsData) return e.overall_status === 'PLAYED';
      const drawn = e.drawn_rounds ?? [];
      return drawn.length === 0 || drawn.includes(activeRound);
    })
    .sort((a, b) => {
      const aScore = computeRoundScoreVsPar(a, activeRound, propHoleParMap, propHoleHcpMap, method);
      const bScore = computeRoundScoreVsPar(b, activeRound, propHoleParMap, propHoleHcpMap, method);
      if (aScore == null && bScore == null) return 0;
      if (aScore == null) return 1;
      if (bScore == null) return -1;
      if (method === 'stableford_net') return bScore - aScore;
      return aScore - bScore;
    })
    .map((entry, idx) => ({ ...entry, position: idx + 1 }));

  const totalPages = Math.max(1, Math.ceil(played.length / PAGE_SIZE));

  useEffect(() => {
    setPage(0);
  }, [layout]);

  useEffect(() => {
    if (layout !== 'scorecard' || totalPages <= 1) return;
    const timer = setInterval(() => {
      setPage((p) => (p + 1) % totalPages);
    }, 8000);
    return () => clearInterval(timer);
  }, [layout, totalPages]);

  const year = tournament?.start_date
    ? new Date(tournament.start_date).getFullYear()
    : '';

  const hasSponsors = partners.length > 0;

  return (
    <div className="flex flex-col h-full bg-golf-gray text-white overflow-hidden">
      <div className="flex items-center justify-between px-3 sm:px-6 lg:px-10 xl:px-16 py-3 sm:py-4 lg:py-6 bg-gradient-to-r from-golf-green to-[#2d7a44] border-b border-white/10 gap-2 sm:gap-3 flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-4 lg:gap-6 min-w-0">
          <div className="h-8 sm:h-12 lg:h-16 xl:h-20 w-auto max-w-[60px] sm:max-w-[100px] lg:max-w-[140px] xl:max-w-[180px] flex items-center justify-center flex-shrink-0">
            {orgLogoUrl ? (
              <img src={orgLogoUrl} alt="Tournament logo" className="h-full w-auto object-contain drop-shadow-lg" />
            ) : (
              <div className="w-8 h-8 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center bg-white/10 shadow-lg">
                <TrophyIcon className="w-5 h-5 sm:w-7 sm:h-7 lg:w-9 lg:h-9 text-white" />
              </div>
            )}
          </div>
          <div className="w-px h-6 sm:h-10 lg:h-14 bg-white/20 hidden sm:block" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3 flex-wrap">
              <p className="text-white/60 text-[10px] sm:text-sm lg:text-base xl:text-lg font-medium uppercase tracking-widest">Live Leaderboard</p>
            </div>
            <h2 className="text-base sm:text-2xl lg:text-3xl xl:text-4xl font-bold font-display truncate leading-tight">
              {tournament?.name ?? 'Tournament'}{year && !tournament?.name?.includes(String(year)) ? ` ${year}` : ''}
            </h2>
            {tournament && (
              <div className="hidden sm:flex items-center gap-3 lg:gap-4 mt-0.5 flex-wrap">
                {tournament.venue && (
                  <span className="text-white/50 text-xs lg:text-sm">{tournament.venue}</span>
                )}
                {tournament.venue && (
                  <span className="text-white/30 text-sm">·</span>
                )}
                <span className="text-white/50 text-xs lg:text-sm">Prize Pool: ₦{(tournament.prize_pool ?? 0).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center bg-black/30 rounded-xl p-1 gap-1 border border-white/10">
            <button
              onClick={() => setLayout('scorecard')}
              title="Scorecard view — Top 15"
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-lg text-[10px] sm:text-xs lg:text-sm font-semibold transition-all ${
                layout === 'scorecard'
                  ? 'bg-golf-orange text-white shadow'
                  : 'text-white/50 hover:text-white hover:bg-white/10'
              }`}
            >
              <TableCellsIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4" />
              <span className="hidden sm:inline">Leaderboard</span>
            </button>
            <button
              onClick={() => setLayout('split')}
              title="Split 3-column view"
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-lg text-[10px] sm:text-xs lg:text-sm font-semibold transition-all ${
                layout === 'split'
                  ? 'bg-golf-orange text-white shadow'
                  : 'text-white/50 hover:text-white hover:bg-white/10'
              }`}
            >
              <ViewColumnsIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4" />
              <span className="hidden sm:inline">Scoreboard</span>
            </button>
          </div>
        </div>
      </div>

      <div className="sm:hidden flex-1 overflow-hidden flex flex-col">
        <MobileLeaderboardList
          entries={played}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          activeRound={activeRound}
          method={method}
        />
      </div>

      <AnimatePresence mode="wait">
        {layout === 'scorecard' ? (
          <motion.div
            key="scorecard"
            className="hidden sm:flex flex-1 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <ScorecardLayout
              played={allPlayed}
              hasSponsors={hasSponsors}
              partners={partners}
              tournament={tournament}
              totalRoundsProp={totalRoundsProp}
              roundNames={roundNames}
              method={method}
              holeParMap={propHoleParMap}
              holeHcpMap={propHoleHcpMap}
            />
          </motion.div>
        ) : (
          <motion.div
            key="split"
            className="hidden sm:flex flex-1 overflow-hidden relative"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <SplitLayout
              played={allPlayed}
              hasSponsors={hasSponsors}
              partners={partners}
              tournament={tournament}
              activeRound={activeRound}
              totalRoundsProp={totalRoundsProp}
              roundNames={roundNames}
              method={method}
              holeParMap={propHoleParMap}
              holeHcpMap={propHoleHcpMap}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <SpecialPrizesStrip prizes={specialPrizes.filter(p => p.round_number === activeRound)} />
    </div>
  );
}
